import { WorkflowExhibit } from "@codegen/enums";
import { StatusTagType } from "@components/StatusTag";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@sentry/react";
import { useStatsigClient } from "@statsig/react-bindings";
import * as changeCase from "change-case";
import React from "react";
import { useFormContext } from "react-hook-form";
import { OnboardingFooter } from "./OnboardingFooter";
import { OnboardingHeader } from "./OnboardingHeader";

interface OnboardingContainerProps {
  children: React.ReactNode;
  onSubmit: (data?: unknown) => void | Promise<void>;
  onBack?: () => void;
  onSkipForNow?: () => void;
  title?: string;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  estimatedTime?: number;
  progress?: number;
  currentStep?: number;
  status?: StatusTagType;
  name?: string;
  exhibit?: WorkflowExhibit;
  exitPath?: string;
  isLoading?: boolean;
  nextButtonText?: string;
  footnote?: React.ReactNode;
  showSkipForNow?: boolean;
  hideExitButton?: boolean;
  isNextDisabled?: boolean;
}

export const OnboardingContainer: React.FC<OnboardingContainerProps> = ({
  children,
  title,
  subtitle,
  description,
  progress,
  currentStep,
  onSubmit,
  onBack,
  onSkipForNow,
  name,
  exhibit,
  exitPath,
  estimatedTime,
  isLoading,
  nextButtonText = "Next",
  footnote,
  showSkipForNow = true,
  hideExitButton = false,
  isNextDisabled = false,
}) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const { client } = useStatsigClient();
  const { handleSubmit, formState } = useFormContext() ?? {};

  React.useEffect(() => {
    if (client && (name || exhibit)) {
      client.logEvent(
        "onboarding_view",
        name ?? `${changeCase.capitalCase(exhibit?.type ?? "")}`,
        {
          onboardingExhibitId: exhibit?.id ?? "",
          exhibitId: exhibit?.exhibitId?.toString() ?? "",
        }
      );
      Sentry.addBreadcrumb({
        category: "onboarding",
        message: `Onboarding Viewed: ${
          name ?? `${changeCase.capitalCase(exhibit?.type ?? "")}`
        }`,
      });
    }
  }, [client, name, exhibit]);

  const processData = (data?: unknown) => {
    Promise.resolve(onSubmit(data)).catch((error) => {
      console.error("Error during onSubmit execution:", error);
    });
  };

  const handleFormSubmit = handleSubmit
    ? handleSubmit(processData)
    : (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (formRef.current) {
          const formData = new FormData(formRef.current);
          const values = formData.entries();
          processData(Object.fromEntries(values));
        } else {
          console.error("Form ref not found for submission");
          processData();
        }
      };

  const handleNextClick = () => {
    if (handleSubmit) {
      void handleSubmit(processData)();
    } else {
      if (formRef.current) {
        const formData = new FormData(formRef.current);
        const values = formData.entries();
        processData(Object.fromEntries(values));
      } else {
        console.error("Form ref not found for submission");
        processData();
      }
    }
  };

  return (
    <ErrorBoundary>
      <div className="w-full h-screen flex flex-col relative overflow-hidden bg-white">
        {/* Header */}
        {title && (
          <OnboardingHeader
            title={title}
            progress={progress ?? 0}
            exitPath={exitPath}
            estimatedTime={estimatedTime}
            hideExitButton={hideExitButton}
          />
        )}
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="xs:mx-4 sm:mx-6 md:mx-12 lg:mx-24 mt-12">
            <div className="flex flex-row w-full justify-center items-start gap-x-6 relative">
              {currentStep !== undefined && (
                <span className="absolute -left-14 hidden lg:flex min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-col justify-center items-center rounded-full border-2 border-azure-radiance-600 text-azure-radiance-600 text-base font-semibold">
                  {currentStep}
                </span>
              )}
              <div className="flex flex-col w-full">
                <form
                  ref={formRef}
                  className="rr-mask"
                  onSubmit={handleFormSubmit}
                >
                  <div className="flex flex-col mb-6">
                    {subtitle && (
                      <h3 className="text-primary font-semibold text-xl leading-7">
                        {subtitle}
                      </h3>
                    )}
                    {description && (
                      <p className="text-primary font-normal text-base">
                        {description}
                      </p>
                    )}
                  </div>
                  {children}
                  {formState?.errors?.root?.serverError && (
                    <div className="text-sm text-red-500 mt-4">
                      There was an error saving your inputs. Please verify they
                      are correct.
                    </div>
                  )}
                  {footnote}
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <OnboardingFooter
          onNext={handleNextClick}
          onBack={onBack}
          onSkipForNow={onSkipForNow}
          isLoading={isLoading}
          nextButtonText={nextButtonText}
          isNextDisabled={
            isNextDisabled ||
            formState?.isSubmitting ||
            formState?.isValidating ||
            isLoading
          }
          showSkipForNow={showSkipForNow}
        />
      </div>
    </ErrorBoundary>
  );
};
